import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  DeleteMenuItemWithConfirmationModal,
  DeleteMenuItemWithConfirmationModalProps,
} from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';

import { useDeleteCashflowMutation } from './graphql/DeleteCashflowMenuItem.generated';

interface DeleteCashflowMenuItemProps
  extends Partial<DeleteMenuItemWithConfirmationModalProps> {
  cashflowId: string;
  onDelete: () => void;
  modalProps?: Record<string, unknown>;
  menuItemProps?: Record<string, unknown>;
}

export function DeleteCashflowMenuItem({
  cashflowId,
  onDelete,
  modalProps = {},
  menuItemProps = {},
  ...deleteProps
}: DeleteCashflowMenuItemProps) {
  const canEdit = useCanEditHouseholdData();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [deleteCashflow, { loading: deleting }] = useDeleteCashflowMutation({
    variables: { id: cashflowId },
    onError: (error) => {
      reportError(`Failed to delete cashflow`, error);
      showFeedback(
        'Failed to delete this cash flow. Please refresh the page and try again.'
      );
    },
    onCompleted: () => {
      showFeedback('Cash flow deleted successfully', { variant: 'success' });
      onDelete();
    },
  });

  if (!canEdit) {
    return null;
  }

  return (
    <DeleteMenuItemWithConfirmationModal
      label="Delete cash flow"
      onConfirmDelete={() => deleteCashflow()}
      {...deleteProps}
      menuItemProps={{ loading: deleting, ...menuItemProps }}
      modalProps={{
        heading: 'Are you sure you want to delete this cash flow?',
        ...modalProps,
      }}
    />
  );
}
